import React, { useCallback, useState } from 'react';
import { IoIosArrowDropleft, IoIosArrowDropright } from 'react-icons/io';
import {
  Box,
  Text,
  Flex,
  Grid,
  GridItem,
  Divider,
  Button,
  IconButton,
} from '@chakra-ui/react';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { handleMonths } from 'utils/functions';

interface MonthProps {
  range: DateProps[];
  setRange: React.Dispatch<React.SetStateAction<DateProps[]>>;
  name?: string;
}

const MonthPicker: React.FC<MonthProps> = ({ range, setRange, name }) => {
  const [year, setYear] = useState(new Date().getFullYear());

  const [hover, setHover] = useState<{ start: Date | null; end: Date | null }>({
    start: null,
    end: null,
  });

  const handleStart = useCallback(
    (calendar: Date) => {
      if (!range[0].startDate) return;

      if (calendar <= new Date(range[0].startDate)) return true;

      return;
    },

    [range],
  );

  const handleEnd = useCallback(
    (calendar: Date) => {
      if (!range[0].endDate) return;

      if (calendar.getTime() >= new Date(range[0].endDate).getTime())
        return true;

      return;
    },
    [range],
  );

  const handleInterval = useCallback(
    (calendar: Date) => {
      if (hover.start && hover.start.getTime() === new Date(calendar).getTime())
        return true;

      if (
        hover.end &&
        hover.start &&
        hover.end >= calendar &&
        hover.start <= calendar
      )
        return true;

      if (!range[0].endDate || !range[0].startDate) return false;

      if (
        new Date(calendar) >=
          new Date(
            range[0].startDate.getFullYear(),
            range[0].startDate.getMonth(),
            1,
          ) &&
        new Date(calendar) <=
          new Date(
            range[0].endDate.getFullYear(),
            range[0].endDate.getMonth(),
            1,
          )
      )
        return true;
      return;
    },
    [range, hover],
  );

  const handlePicker = (item: { abr: string; start: string; end: string }) => {
    if (range[0].startDate && range[0].endDate) {
      return (
        setRange(state => [
          { ...state[0], endDate: undefined, startDate: new Date(item.start) },
        ]),
        setHover(state => ({ ...state, start: new Date(item.start) }))
      );
    }

    return (
      setRange(state => [{ ...state[0], endDate: new Date(item.end) }]),
      setHover({ start: null, end: null })
    );
  };

  return (
    <Box
      alignItems="center"
      justifyContent="center"
      borderRadius="8px"
      mt="60px"
    >
      <Flex alignItems="center" justifyContent="center">
        <IconButton
          aria-label=""
          variant="ghost"
          onClick={() => setYear(state => state - 1)}
          icon={<IoIosArrowDropleft />}
          h="20px"
          w="20px"
          color="text.100"
          alignSelf="center"
          mr="auto"
          ml="16px"
        />
        <Text fontSize="md" color="text.100">
          {year}
        </Text>
        <IconButton
          aria-label=""
          variant="ghost"
          onClick={() => setYear(state => state + 1)}
          icon={<IoIosArrowDropright />}
          h="20px"
          w="20px"
          color="text.100"
          alignSelf="center"
          ml="auto"
          mr="16px"
        />
      </Flex>
      <Divider mt="8px" bg="gray.700" h="0.5px" />

      <Grid mx="16px" templateColumns="repeat(3, 1fr)">
        {handleMonths(year).map(item => {
          return (
            <GridItem
              onMouseEnter={() =>
                hover.start &&
                setHover(state => ({ ...state, end: new Date(item.end) }))
              }
              onMouseLeave={() =>
                hover.end && setHover(state => ({ ...state, end: null }))
              }
              fontWeight={400}
              as={Button}
              _hover={{
                bg: handleInterval(new Date(item.start)) ? 'blue.100' : 'white',
                borderRightRadius:
                  hover.end?.getTime() === new Date(item.end).getTime() ||
                  handleEnd(new Date(item.end))
                    ? '8px'
                    : '0px',
              }}
              onClick={() => handlePicker(item)}
              mt="20px"
              key={item.abr}
              bg={handleInterval(new Date(item.start)) ? 'blue.100' : 'white'}
              borderLeftRadius={
                handleStart(new Date(item.start)) ? '8px' : '0px'
              }
              borderRightRadius={handleEnd(new Date(item.end)) ? '8px' : '0px'}
            >
              <Text
                data-cy={`picker-${item.abr}-${name}`}
                textAlign="center"
                color={
                  handleInterval(new Date(item.start)) ? 'white' : 'text.100'
                }
                fontSize="sm"
              >
                {item.abr}
              </Text>
            </GridItem>
          );
        })}
      </Grid>
    </Box>
  );
};

export default MonthPicker;
